import React, { useEffect, Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { HomePage } from './pages/HomePage'
import { PrivacyPolicy } from './pages/PrivacyPolicy'
import { TermsOfService } from './pages/TermsOfService'

// Lazy load heavy pages
const EditorPage = lazy(() => import('./pages/EditorPage').then(module => ({ default: module.EditorPage })))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then(module => ({ default: module.PrivacyPage })))
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(module => ({ default: module.SettingsPage })))
const ClubCreationPage = lazy(() => import('./pages/ClubCreationPage').then(module => ({ default: module.ClubCreationPage })))
const ClubManagePage = lazy(() => import('./pages/ClubManagePage').then(module => ({ default: module.ClubManagePage })))
const ClubsPage = lazy(() => import('./pages/ClubsPage').then(module => ({ default: module.ClubsPage })))
const ClubPage = lazy(() => import('./pages/ClubPage').then(module => ({ default: module.ClubPage })))
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))
const CollectiblePOIsPage = lazy(() => import('./pages/CollectiblePOIsPage'))
const HazardModerationPage = lazy(() => import('./pages/HazardModerationPage').then(module => ({ default: module.HazardModerationPage })))
const RouteViewPage = lazy(() => import('./pages/RouteViewPage').then(module => ({ default: module.RouteViewPage })))
const RouteSlugView = lazy(() => import('./pages/RouteSlugView').then(module => ({ default: module.RouteSlugView })))
const CalendarPage = lazy(() => import('./pages/CalendarPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(module => ({ default: module.NotFoundPage })))
const MapStyleSandboxPage = lazy(() => import('./pages/MapStyleSandboxPage').then(module => ({ default: module.MapStyleSandboxPage })))
const SetAdminRole = lazy(() => import('./pages/SetAdminRole').then(module => ({ default: module.SetAdminRole })))
import { authInitializer } from './services/authInitializer'
import { ErrorBoundary } from './components/common/ErrorBoundary'
import { LoadingSpinner } from './components/common/LoadingSpinner'
import { ProfileCompletionModal } from './components/forms/ProfileCompletionModal'

// Lazy load embed components
const EmbedViewerPage = React.lazy(() => import('./pages/EmbedViewerPage'))
const EmbedEditorPage = React.lazy(() => import('./pages/EmbedEditorPage'))
const EmbedRoutePage = React.lazy(() => import('./pages/EmbedRoutePage'))


// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
})

function App() {
  // Initialize Firebase auth state synchronization on app start
  useEffect(() => {
    authInitializer.initialize()
    
    // Cleanup on unmount
    return () => {
      authInitializer.cleanup()
    }
  }, [])

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ProfileCompletionModal />
          <div className="min-h-screen bg-gray-900">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/calendar" element={<Suspense fallback={<LoadingSpinner />}><CalendarPage /></Suspense>} />
              <Route path="/clubs" element={<Suspense fallback={<LoadingSpinner />}><ClubsPage /></Suspense>} />
              <Route path="/club/:slug" element={<Suspense fallback={<LoadingSpinner />}><ClubPage /></Suspense>} />
              <Route path="/editor" element={<Suspense fallback={<LoadingSpinner />}><EditorPage /></Suspense>} />
              <Route path="/privacy" element={<Suspense fallback={<LoadingSpinner />}><PrivacyPage /></Suspense>} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/settings" element={<Suspense fallback={<LoadingSpinner />}><SettingsPage /></Suspense>} />
              <Route path="/create-club" element={<Suspense fallback={<LoadingSpinner />}><ClubCreationPage /></Suspense>} />
              <Route path="/club/:clubId/manage" element={<Suspense fallback={<LoadingSpinner />}><ClubManagePage /></Suspense>} />
              <Route path="/admin" element={<Suspense fallback={<LoadingSpinner />}><AdminDashboard /></Suspense>} />
              <Route path="/admin/collectible-pois" element={<Suspense fallback={<LoadingSpinner />}><CollectiblePOIsPage /></Suspense>} />
              <Route path="/admin/hazards" element={<Suspense fallback={<LoadingSpinner />}><HazardModerationPage /></Suspense>} />
              <Route path="/sandbox/map-style" element={<Suspense fallback={<LoadingSpinner />}><MapStyleSandboxPage /></Suspense>} />
              <Route path="/set-admin-role" element={<Suspense fallback={<LoadingSpinner />}><SetAdminRole /></Suspense>} />
              
              {/* Embed routes - slug-based for SEO */}
              <Route path="/embed/:clubSlug/:embedSlug" element={
                <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div></div>}>
                  <EmbedViewerPage />
                </Suspense>
              } />
              <Route path="/embed/route/:routeId" element={
                <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div></div>}>
                  <EmbedRoutePage />
                </Suspense>
              } />
              
              {/* Embed editor routes - slug-based for SEO */}
              <Route path="/club/:clubSlug/embeds/new" element={
                <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div></div>}>
                  <EmbedEditorPage />
                </Suspense>
              } />
              <Route path="/club/:clubSlug/embeds/:embedSlug/edit" element={
                <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div></div>}>
                  <EmbedEditorPage />
                </Suspense>
              } />
              
              {/* Hierarchical slug routes - must come before generic :routeId */}
              <Route path="/route/:country/:state/:name" element={<Suspense fallback={<LoadingSpinner />}><RouteSlugView /></Suspense>} />
              <Route path="/route/:routeId" element={<Suspense fallback={<LoadingSpinner />}><RouteViewPage /></Suspense>} />
              {/* Club slug routes - must be last to avoid conflicts */}
              <Route path="/:clubSlug" element={<HomePage />} />
              {/* 404 catch-all route - must be last */}
              <Route path="*" element={<Suspense fallback={<LoadingSpinner />}><NotFoundPage /></Suspense>} />
            </Routes>
          </div>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
